import React, { useEffect, useReducer, useRef, useState } from "react";

import { Input, InputChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { jointCommitteeReducer } from "../../../state/reducers";
import { IJointCommittee } from "../../../utils/types/models";

const JointCommitteeEditor: React.FC<IAddEntityScreenProps<IJointCommittee> | IEditEntityScreenProps<IJointCommittee>> = (
	props: IAddEntityScreenProps<IJointCommittee> | IEditEntityScreenProps<IJointCommittee>
) => {
	const { t } = useTranslation();

	const [state, dispatch] = useReducer(jointCommitteeReducer, getInitialState<IJointCommittee>());
	const initialJointCommittee: IJointCommittee = {
		id: (props as IEditEntityScreenProps<IJointCommittee>).recordId || 0,
		name: "",
		active: true
	};
	const [jointCommittee, setJointCommittee] = useState<IJointCommittee>(initialJointCommittee);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setJointCommittee(state.entity);
		}
	}, [state.entity]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent): void {
		setJointCommittee(handleChange(jointCommittee, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(jointCommittee.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: IJointCommittee): void {
		if (record && !close) {
			setJointCommittee(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<IJointCommittee>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={jointCommittee}
			endpoint={Endpoint.JointCommittees}
			entityState={state}
			entityType={t("jointCommittee")}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={jointCommittee.name}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} value={jointCommittee.name} onChange={onChange} required disabled={readonly} />
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={jointCommittee.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default JointCommitteeEditor;
